// src/pages/ordens-servico/OrdensServicoExecucao.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { escopoPermissao, pode, temNivelMinimo, nivelUsuario } from "../../utils/permissoes";
import { useIsMobile } from "../../hooks/useIsMobile";
import LayoutDesktop from "../../layouts/LayoutDesktop";
import LayoutMobile from "../../layouts/LayoutMobile";

import { db } from "../../config/firebase";
import { NIVEIS } from "../../config/niveis";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  updateDoc,
  onSnapshot,
} from "firebase/firestore";

import {
  IconCalendar,
  IconMachine,
  IconSector,
  IconBuilding,
  IconUser,
  IconKey,
  IconTool,
  IconList,
  IconClock,
} from "../../components/Icons/Icons";

function renderCodigoOs(os) {
  if (os.codigo) return os.codigo;
  if (os.codigoSequencial != null) {
    return `OS${String(os.codigoSequencial).padStart(4, "0")}`;
  }
  if (os.id) return os.id.slice(0, 8);
  return "-";
}

function getCriticidadeConfig(criticidade) {
  const valor = (criticidade || "").toLowerCase();

  if (valor === "alta" || valor === "muito alta") {
    return { texto: "Alta", classe: "badge-alta" };
  }
  if (valor === "média" || valor === "media") {
    return { texto: "Média", classe: "badge-media" };
  }
  if (valor === "baixa") {
    return { texto: "Baixa", classe: "badge-baixa" };
  }
  return null;
}

const STATUS_NAO_EXECUTAVEIS = [
  "aguardando material",
  "aguardando parada de máquina",
  "aguardando parada de maquina",
  "serviço externo",
  "servico externo",
  "solicitar material",
  "aguardando autorização",
  "aguardando autorizacao",
  "cancelada",
  "cancelado",
  "finalizada",
  "finalizado",
];

function isStatusExecutavel(os) {
  const nome = (os.statusNome || "").toLowerCase().trim();
  if (!nome) return true;
  return !STATUS_NAO_EXECUTAVEIS.some((palavra) => nome.includes(palavra));
}

async function ajustarStatusAberta24h(listaOs) {
  const agora = new Date();
  const updates = [];

  for (const os of listaOs) {
    const nome = (os.statusNome || "").toLowerCase();
    const id = os.statusId || "";
    const ts = os.criadoEm;

    if (!ts?.toDate || !os.id) continue;

    const diffHoras =
      (agora.getTime() - ts.toDate().getTime()) / (1000 * 60 * 60);
    const isAberta24h =
      id === "fab_aberta_24h" || nome.includes("aberta 24h");

    if (isAberta24h && diffHoras >= 24) {
      const ref = doc(db, "ordensServico", os.id);
      const novoStatusId = "fab_aberta";
      const novoStatusNome = "Aberta";
      const novoStatusCor = "#2563eb";

      // Atualiza em memória para refletir imediatamente na tela
      os.statusId = novoStatusId;
      os.statusNome = novoStatusNome;
      os.statusCor = novoStatusCor;

      updates.push(
        updateDoc(ref, {
          statusId: novoStatusId,
          statusNome: novoStatusNome,
          statusCor: novoStatusCor,
        }).catch((e) => {
          console.error("Erro ao atualizar status Aberta 24H:", e);
        })
      );
    }
  }

  if (updates.length) {
    try {
      await Promise.all(updates);
    } catch (e) {
      console.error("Erro ao concluir atualizações de status:", e);
    }
  }
}


function isMaquinaChave(os) {
  const valor = os.maquinaChave;
  if (typeof valor === "boolean") return valor;
  const txt = String(valor || "").toLowerCase();
  return txt === "sim" || txt === "true";
}


function getDiasEmAberto(os) {
  const ts = os.criadoEm;
  if (!ts?.toDate) return null;
  try {
    const criado = ts.toDate();
    const agora = new Date();
    const diffMs = agora.getTime() - criado.getTime();
    if (!Number.isFinite(diffMs) || diffMs < 0) return null;
    return diffMs / (1000 * 60 * 60 * 24);
  } catch (e) {
    console.error("Erro ao calcular dias em aberto da OS:", e);
    return null;
  }
}

function getNivelCriticidade(os) {
  const valor = (os.criticidade || "").toLowerCase();
  if (valor === "alta" || valor === "muito alta") return "alta";
  if (valor === "baixa") return "baixa";
  return "media";
}

function getBucketPrioridade(os) {
  const tipoServico = String(os.tipoServico || "").toLowerCase();
  const diasEmAberto = getDiasEmAberto(os);

  // Regras especiais para OS de Preventiva atrasada:
  // - Preventiva aberta há 15+ dias -> amarelo (antes de "máquina não parada / não chave / baixa")
  // - Preventiva aberta há 10+ dias -> verde
  // - Preventiva aberta há 3+ dias  -> amarelo (preventivas semanais / curto prazo)
  if (tipoServico === "preventiva" && diasEmAberto != null) {
    if (diasEmAberto >= 15) {
      return 8; // amarelo
    }
    if (diasEmAberto >= 10) {
      return 11; // verde
    }
    if (diasEmAberto >= 3) {
      return 7; // amarelo
    }
  }


  const parada = (typeof os?.maquinaParadaAtiva === "boolean")
    ? os.maquinaParadaAtiva
    : (String(os.maquinaParou || "").toLowerCase().trim() === "sim" && !os?.maquinaVoltouRodarEm);
  const chave = isMaquinaChave(os);
  const crit = getNivelCriticidade(os);

  if (parada && chave && crit === "alta") return 1;
  if (parada && chave && crit === "media") return 2;
  if (parada && chave && crit === "baixa") return 3;
  if (!parada && chave && crit === "alta") return 4;
  if (parada && !chave && crit === "alta") return 5;
  if (!parada && chave && crit === "media") return 6;
  if (!parada && chave && crit === "baixa") return 7;
  if (parada && !chave && crit === "media") return 8;
  if (parada && !chave && crit === "baixa") return 9;
  if (!parada && !chave && crit === "alta") return 10;
  if (!parada && !chave && crit === "media") return 12;
  if (!parada && !chave && crit === "baixa") return 13;
  return 99;
}

function getBucketClass(os) {
  const bucket = getBucketPrioridade(os);
  if (bucket >= 1 && bucket <= 4) return "bucket-alta";
  if (bucket >= 5 && bucket <= 9) return "bucket-media";
  if (bucket >= 10 && bucket <= 13) return "bucket-baixa";
  return "";
}

function sortPorPrioridade(a, b) {
  const bucketA = getBucketPrioridade(a);
  const bucketB = getBucketPrioridade(b);
  if (bucketA !== bucketB) return bucketA - bucketB;
  const tA = a.criadoEm?.toMillis?.() ?? 0;
  const tB = b.criadoEm?.toMillis?.() ?? 0;
  return tA - tB;
}

function formatarData(ts) {
  if (!ts?.toDate) return "-";
  const d = ts.toDate();
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function ChevronIcon({ expanded }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        transition: "transform 0.2s ease",
        transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
      }}
    >
      <polyline points="6 9 12 15 18 9"></polyline>
    </svg>
  );
}

function OrdensServicoExecucao() {
  const { tema, alterarTema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile(900);

  const [ordens, setOrdens] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [sidebarAberta, setSidebarAberta] = useState(false);
  const [viewMode, setViewMode] = useState("prioridade");
  const [expandedId, setExpandedId] = useState(null);

  function toggleExpand(id) {
    setExpandedId((prev) => (prev === id ? null : id));
  }

  useEffect(() => {
    if (!usuarioLogado?.grupoId) return;

    setCarregando(true);
    setErro("");

    const osRef = collection(db, "ordensServico");
    // ✅ Aqui também precisa bater com a tabela de permissões.
    // Esta tela é derivada da visão por status/prioridade.
    const escopo = escopoPermissao(usuarioLogado, "ordensServico", "porStatus", "tabela");
    const filtros = [where("grupoId", "==", usuarioLogado.grupoId)];
    if (escopo === "proprias" && usuarioLogado?.uid) {
      filtros.push(where("criadoPor", "==", usuarioLogado.uid));
    }
    const qOs = query(osRef, ...filtros);

    const unsubscribe = onSnapshot(
      qOs,
      async (snapOs) => {
        try {
          const listaOs = snapOs.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          }));

          await ajustarStatusAberta24h(listaOs);

          setOrdens(listaOs);
        } catch (e) {
          console.error("Erro ao processar OS (execução):", e);
          setErro("Erro ao processar OS executáveis.");
        } finally {
          setCarregando(false);
        }
      },
      (error) => {
        console.error("Erro ao escutar OS (execução):", error);
        setErro("Erro ao carregar OS executáveis. Tente novamente.");
        setCarregando(false);
      }
    );

    return () => unsubscribe();
  }, [usuarioLogado]);

  const ordensExecutaveis = useMemo(
    () => ordens.filter((os) => isStatusExecutavel(os)),
    [ordens]
  );

  // Usa normalização (aceita número, string numérica e texto como "Cliente")
  const nivelAtual = nivelUsuario(usuarioLogado);
  const isCliente = nivelAtual === NIVEIS.CLIENTE;

  // 🔒 Apenas Técnico+ pode iniciar atendimento (independente de customizações de permissão)
  const podeIniciarAtendimento = temNivelMinimo(usuarioLogado, NIVEIS.TECNICO);

  // Cliente: deve enxergar apenas as próprias OS (criadas por ele)
  const ordensBase = useMemo(() => {
    if (!isCliente) return ordensExecutaveis;
    const uid = usuarioLogado?.uid;
    return uid ? ordensExecutaveis.filter((os) => os.criadoPor === uid) : [];
  }, [isCliente, ordensExecutaveis, usuarioLogado]);

  const ordensPorPrioridade = useMemo(
    () => [...ordensBase].sort(sortPorPrioridade),
    [ordensBase]
  );

  const ordensPorData = useMemo(
    () =>
      [...ordensBase].sort((a, b) => {
        const tA = a.criadoEm?.toMillis?.() ?? 0;
        const tB = b.criadoEm?.toMillis?.() ?? 0;
        return tA - tB;
      }),
    [ordensBase]
  );

  function handleIniciarAtendimento(osId) {
    if (!osId) return;
    // 🔒 Cliente/Kiosk não podem iniciar atendimento
    if (!podeIniciarAtendimento) return;

    // Respeita também a matriz de permissões (se alguém subir o mínimo acima de Técnico)
    const canIniciar = pode(usuarioLogado, "ordensServico", "meusAtendimentos", "tabela");
    if (!canIniciar) return;

    navigate(`/ordens-servico/atendimento/${osId}`);
  }

  const getInitials = (text) => {
    if (!text) return "OS";
    return text.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase();
  };

  function renderCard(os, index) {
    const cfgCrit = getCriticidadeConfig(os.criticidade);
    const bucketClass = getBucketClass(os);
    const paradaTxt = String(os.maquinaParou || "-");
    const chaveTxt = isMaquinaChave(os) ? "Sim" : "Não";
    const fotoUrl = os.fotoAberturaUrl || (Array.isArray(os.fotosAbertura) ? os.fotosAbertura[0] : null);

    return (
      <article
        key={os.id}
        className={`m-fila-card ${bucketClass}`}
        style={{ animationDelay: `${index * 0.04}s` }}
      >
        <div className="m-fila-card-header">
          <div className="m-fila-avatar">
            {getInitials(os.maquinaNome)}
          </div>
          <div className="m-fila-info">
            <span className="m-fila-code">{renderCodigoOs(os)}</span>
            <h4 className="m-fila-machine">
              {os.maquinaNome || "Sem máquina"}
            </h4>
</div>
          {cfgCrit && (
            <span className={`m-fila-badge ${cfgCrit.classe}`}>
              {cfgCrit.texto}
            </span>
          )}
          {fotoUrl && (
            <button
              type="button"
              className="os-thumb-btn m-fila-photo-btn"
              title="Abrir foto da OS"
              onClick={(e) => {
                e.stopPropagation();
                window.open(fotoUrl, "_blank");
              }}
            >
              <img
                className="os-thumb-img m-fila-photo"
                src={fotoUrl}
                alt="Foto da OS"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </button>
          )}
        </div>

        <p className="m-fila-description">
          {os.titulo || os.descricao || "Sem descrição"}
        </p>

        <div className="m-fila-meta">
          <div className="m-fila-meta-item">
            <IconSector size="14px" />
            <span>{os.setorNome || "-"}</span>
          </div>
          <div className="m-fila-meta-item">
            <IconClock size="14px" />
            <span>{formatarData(os.criadoEm)}</span>
          </div>
          <div className="m-fila-meta-item">
            <IconTool size="14px" />
            <span>Parou: {paradaTxt}</span>
          </div>
          {isMaquinaChave(os) && (
            <div className="m-fila-meta-item m-fila-chave">
              <IconKey size="14px" />
              <span>Máq. Chave</span>
            </div>
          )}
        </div>

        {(() => {
          if (!podeIniciarAtendimento) return null;
          const canIniciar = pode(usuarioLogado, "ordensServico", "meusAtendimentos", "tabela");
          if (!canIniciar) return null;
          return (
            <button
              className="m-fila-action-btn"
              onClick={() => handleIniciarAtendimento(os.id)}
            >
              <IconTool size="18px" />
              Iniciar Atendimento
            </button>
          );
        })()}
      </article>
    );
  }

  const listaExibida = viewMode === "prioridade" ? ordensPorPrioridade : ordensPorData;

  function getRowPrioridadeStyle(os) {
    const bucket = getBucketPrioridade(os);
    if (bucket >= 1 && bucket <= 4) {
      return {
        background: "linear-gradient(90deg, rgba(220, 38, 38, 0.25) 0%, rgba(220, 38, 38, 0.08) 100%)",
        borderLeft: "5px solid #dc2626",
      };
    }
    if (bucket >= 5 && bucket <= 9) {
      return {
        background: "linear-gradient(90deg, rgba(245, 158, 11, 0.25) 0%, rgba(245, 158, 11, 0.08) 100%)",
        borderLeft: "5px solid #f59e0b",
      };
    }
    if (bucket >= 10 && bucket <= 13) {
      return {
        background: "linear-gradient(90deg, rgba(34, 197, 94, 0.25) 0%, rgba(34, 197, 94, 0.08) 100%)",
        borderLeft: "5px solid #22c55e",
      };
    }
    return { borderLeft: "5px solid #64748b" };
  }

  function getPrioridadeLabel(os) {
    const bucket = getBucketPrioridade(os);
    if (bucket >= 1 && bucket <= 4) return { texto: "ALTA", classe: "prioridade-alta" };
    if (bucket >= 5 && bucket <= 9) return { texto: "MÉDIA", classe: "prioridade-media" };
    if (bucket >= 10 && bucket <= 13) return { texto: "BAIXA", classe: "prioridade-baixa" };
    return { texto: "-", classe: "" };
  }

  const conteudoMobile = (
    <>
      <div className="m-color-bar">
        <span className="m-color-dot" style={{ background: "#FFFFFF" }}></span>
        <span className="m-color-dot" style={{ background: "#808080" }}></span>
        <span className="m-color-dot" style={{ background: "#F5F5DC" }}></span>
        <span className="m-color-dot" style={{ background: "#1a1a1a" }}></span>
        <span className="m-color-dot" style={{ background: "#DC2626" }}></span>
      </div>

      <div className="m-actions">
        <button
          className="m-btn-pill m-btn-primary"
          onClick={() => navigate("/ordens-servico")}
        >
          Fila de Atendimento
        </button>
        <button
          className="m-btn-pill m-btn-secondary"
          onClick={() => navigate("/ordens-servico/status")}
        >
          Por Status
        </button>
      </div>

      {erro && (
        <div className="m-error-banner">
          {erro}
        </div>
      )}

      {carregando ? (
        <div className="m-loading-state">
          <span className="m-loading-spinner">
            <IconClock size="40px" />
          </span>
          <p>Carregando ordens de serviço...</p>
        </div>
      ) : ordensBase.length === 0 ? (
        <div className="m-empty-state">
          <span className="m-empty-icon">
            <IconTool size="48px" color="var(--cor-texto-apagado)" />
          </span>
          <p>Nenhuma OS executável encontrada</p>
          <span className="m-empty-hint">Todas as ordens estão aguardando material ou parada</span>
        </div>
      ) : (
        <>
          <div className="m-results-count">
            {listaExibida.length} {listaExibida.length === 1 ? "ordem" : "ordens"} na fila
          </div>
          <section className="m-user-list">
            {listaExibida.map((os, index) => renderCard(os, index))}
          </section>
        </>
      )}
    </>
  );

  if (isMobile) {
    return (
      <LayoutMobile
        tema={tema}
        alterarTema={alterarTema}
        titulo="Fila de Atendimento"
        sidebarAberta={sidebarAberta}
        onToggleSidebar={() => setSidebarAberta(!sidebarAberta)}
        abaBottom="atend"
      >
        {conteudoMobile}
      </LayoutMobile>
    );
  }

  return (
    <LayoutDesktop
      tema={tema}
      titulo="Fila de Atendimento"
      subtitulo=""
    >
      <section className="pc-card pc-table-card">
        <header className="pc-table-header">
          <div className="pc-table-header-text">
            <h3>Fila de Execução</h3>
            <p>
              Apenas OS executáveis. Status como
              <strong> Aguardando Material</strong> ou{" "}
              <strong>Aguardando Parada</strong> não aparecem aqui.
            </p>
          </div>
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <button
              type="button"
              className="pc-toggle-btn"
              onClick={() => navigate("/ordens-servico/status")}
            >
              <IconCalendar size="16px" /> Por Status
            </button>
            <div className="pc-view-toggle">
              <button
                className={`pc-toggle-btn ${viewMode === "prioridade" ? "active" : ""}`}
                onClick={() => setViewMode("prioridade")}
              >
                <IconList size="16px" /> Prioridade
              </button>
              <button
                className={`pc-toggle-btn ${viewMode === "lista" ? "active" : ""}`}
                onClick={() => setViewMode("lista")}
              >
                <IconCalendar size="16px" /> Data
              </button>
            </div>
          </div>
        </header>
        
        {erro && (
          <div style={{ 
            padding: "12px 20px", 
            background: "rgba(220, 38, 38, 0.15)", 
            color: "#fca5a5",
            borderBottom: "1px solid rgba(220, 38, 38, 0.3)"
          }}>
            {erro}
          </div>
        )}

        {carregando ? (
          <div style={{ padding: "60px", textAlign: "center", color: "var(--cor-texto-apagado)" }}>
            <IconClock size="48px" />
            <p style={{ marginTop: "16px" }}>Carregando ordens de serviço...</p>
          </div>
        ) : ordensBase.length === 0 ? (
          <div style={{ padding: "60px", textAlign: "center", color: "var(--cor-texto-apagado)" }}>
            <IconTool size="48px" />
            <p style={{ marginTop: "16px" }}>Nenhuma OS executável encontrada</p>
            <span style={{ fontSize: "0.85rem", opacity: 0.7 }}>Todas as ordens estão aguardando material ou parada</span>
          </div>
        ) : (
          <>
            <div style={{ 
              padding: "12px 20px", 
              background: "rgba(255,255,255,0.03)",
              borderBottom: "1px solid rgba(255,255,255,0.1)",
              fontSize: "0.85rem",
              color: "var(--cor-texto-apagado)"
            }}>
              {listaExibida.length} {listaExibida.length === 1 ? "ordem" : "ordens"} na fila
            </div>
            
            <div className="pc-table-scroll">
              <table className="pc-table execucao-table">
                <thead>
                  <tr>
                    <th style={{ width: "40px" }}></th>
                    <th style={{ width: "90px" }}>Prioridade</th>
                    <th style={{ width: "100px" }}>Código</th>
                    <th style={{ width: "64px" }}>Foto</th>
                    <th>Máquina</th>
                    <th>Descrição</th>
                    <th>Setor</th>
                    <th style={{ width: "140px" }}>Aberta em</th>
                    <th style={{ width: "120px" }}>Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {listaExibida.map((os) => {
                    const rowStyle = getRowPrioridadeStyle(os);
                    const prioLabel = getPrioridadeLabel(os);
                    const cfgCrit = getCriticidadeConfig(os.criticidade);
                    const isExpanded = expandedId === os.id;
                      const fotoUrl = os.fotoAberturaUrl || (Array.isArray(os.fotosAbertura) ? os.fotosAbertura[0] : null);
                    
                    return (
                      <React.Fragment key={os.id}>
                        <tr 
                          style={{ ...rowStyle, cursor: "pointer" }}
                          onClick={() => toggleExpand(os.id)}
                        >
                          <td style={{ textAlign: "center" }}>
                            <ChevronIcon expanded={isExpanded} />
                          </td>
                          <td>
                            <span className={`pc-prioridade-badge ${prioLabel.classe}`}>
                              {prioLabel.texto}
                            </span>
                          </td>
                          <td>
                            <strong>{renderCodigoOs(os)}</strong>
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {fotoUrl ? (
                              <button
                                type="button"
                                className="os-thumb-btn"
                                title="Abrir foto da OS"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(fotoUrl, "_blank");
                                }}
                              >
                                <img
                                  className="os-thumb-img"
                                  src={fotoUrl}
                                  alt="Foto da OS"
                                  loading="lazy"
                                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                                />
                              </button>
                            ) : (
                              <span style={{ opacity: 0.6 }}>-</span>
                            )}
                          </td>
                          <td>
                            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                              <span style={{ fontWeight: 500 }}>{os.maquinaNome || "-"}</span>
</div>
                          </td>
                          <td>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <span style={{ 
                                maxWidth: "250px", 
                                overflow: "hidden", 
                                textOverflow: "ellipsis", 
                                whiteSpace: "nowrap" 
                              }}>
                                {os.titulo || os.descricao || "-"}
                              </span>
                              {cfgCrit && (
                                <span className={`pc-crit-badge ${cfgCrit.classe}`}>
                                  {cfgCrit.texto}
                                </span>
                              )}
                            </div>
                          </td>
                          <td>{os.setorNome || "-"}</td>
                          <td style={{ fontSize: "0.85rem" }}>{formatarData(os.criadoEm)}</td>
                          <td>
                            {podeIniciarAtendimento &&
                            pode(usuarioLogado, "ordensServico", "meusAtendimentos", "tabela") ? (
                              <button
                                className="pc-btn-iniciar"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleIniciarAtendimento(os.id);
                                }}
                              >
                                <IconTool size="14px" /> Iniciar
                              </button>
                            ) : (
                              <span style={{ opacity: 0.6, fontSize: "0.85rem" }}>
                                -
                              </span>
                            )}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className="row-details-expanded">
                            <td colSpan="9" style={{ padding: 0 }}>
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                                  gap: "16px",
                                  padding: "20px 24px",
                                  background: "rgba(255,255,255,0.03)",
                                  borderTop: "1px solid rgba(255,255,255,0.08)",
                                }}
                              >
                                {fotoUrl && (
                                  <div className="detail-group">
                                    <span className="detail-label">Foto</span>
                                    <button
                                      type="button"
                                      className="os-thumb-btn"
                                      title="Abrir foto da OS"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        window.open(fotoUrl, "_blank");
                                      }}
                                    >
                                      <img
                                        className="os-thumb-img"
                                        src={fotoUrl}
                                        alt="Foto da OS"
                                        loading="lazy"
                                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                                      />
                                    </button>
                                  </div>
                                )}
                                <div className="detail-group">
                                  <span className="detail-label">Empresa</span>
                                  <span className="detail-value">{os.empresaNome || "-"}</span>
                                </div>
                                <div className="detail-group">
                                  <span className="detail-label">Setor</span>
                                  <span className="detail-value">{os.setorNome || "-"}</span>
                                </div>
                                <div className="detail-group">
                                  <span className="detail-label">Máquina Parou</span>
                                  <span className={`detail-value ${((typeof os?.maquinaParadaAtiva === "boolean") ? os.maquinaParadaAtiva : (String(os.maquinaParou || "").toLowerCase().trim() === "sim" && !os?.maquinaVoltouRodarEm)) ? "text-danger" : ""}`}>
                                    {os.maquinaParou || "-"}
                                  </span>
                                </div>
                                <div className="detail-group">
                                  <span className="detail-label">Máquina Chave</span>
                                  <span className="detail-value">{isMaquinaChave(os) ? "Sim" : "Não"}</span>
                                </div>
                                <div className="detail-group">
                                  <span className="detail-label">Criticidade</span>
                                  <span className="detail-value">{os.criticidade || "-"}</span>
                                </div>
                                <div className="detail-group">
                                  <span className="detail-label">Tipo de Serviço</span>
                                  <span className="detail-value">{os.tipoServico || "-"}</span>
                                </div>
                                <div className="detail-group">
                                  <span className="detail-label">Status Atual</span>
                                  <span 
                                    className="detail-value"
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "6px"
                                    }}
                                  >
                                    <span
                                      style={{
                                        width: "10px",
                                        height: "10px",
                                        borderRadius: "50%",
                                        background: os.statusCor || "#64748b",
                                      }}
                                    />
                                    {os.statusNome || "-"}
                                  </span>
                                </div>
                                <div className="detail-group">
                                  <span className="detail-label">Aberta por</span>
                                  <span className="detail-value">{os.criadoPorNome || "-"}</span>
                                </div>
                                {os.descricao && os.descricao !== os.titulo && (
                                  <div className="detail-group" style={{ gridColumn: "1 / -1" }}>
                                    <span className="detail-label">Descrição Completa</span>
                                    <span className="detail-value" style={{ whiteSpace: "pre-wrap" }}>
                                      {os.descricao}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
    </LayoutDesktop>
  );
}

export default OrdensServicoExecucao;
